import React, { useEffect, useState } from 'react';
import { hrmsMyProfile, hrmsMyLeaves, hrmsRequestLeave, hrmsUpdateEmployee, hrmsClockIn, hrmsClockOut, hrmsMyAttendance, hrmsMyPayslips, hrmsMyReferralLink, hrmsMyReferrals } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { CalendarPlus, User, Play, Square, Download, Clock, Copy, Share2, Users, Trophy } from 'lucide-react';
import { generatePayslipPdf } from '../../lib/payslipPdf';

const todayStr = () => {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000); // IST
  return d.toISOString().slice(0, 10);
};

const MePage = () => {
  const { toast } = useToast();
  const [prof, setProf] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [referralLink, setReferralLink] = useState(null);
  const [referrals, setReferrals] = useState({ referrals: [], stats: {} });
  const [edit, setEdit] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leave, setLeave] = useState({ leave_type: 'Casual', from_date: '', to_date: '', reason: '' });
  const [clockBusy, setClockBusy] = useState(false);

  const load = async () => {
    try { setProf(await hrmsMyProfile()); } catch { setProf(null); }
    try { setLeaves(await hrmsMyLeaves()); } catch { setLeaves([]); }
    try { setAttendance(await hrmsMyAttendance()); } catch { setAttendance([]); }
    try { setPayslips(await hrmsMyPayslips()); } catch { setPayslips([]); }
    try { setReferralLink(await hrmsMyReferralLink()); } catch { setReferralLink(null); }
    try { setReferrals(await hrmsMyReferrals()); } catch { setReferrals({ referrals: [], stats: {} }); }
  };
  useEffect(() => { load(); }, []);

  const todayRec = (attendance || []).find((a) => a.date === todayStr());

  const clockIn = async () => {
    setClockBusy(true);
    try { await hrmsClockIn(); toast({ title: 'Clocked in' }); load(); }
    catch (err) { toast({ title: 'Clock-in failed', description: err?.response?.data?.detail || '' }); }
    finally { setClockBusy(false); }
  };
  const clockOut = async () => {
    setClockBusy(true);
    try { await hrmsClockOut(); toast({ title: 'Clocked out' }); load(); }
    catch (err) { toast({ title: 'Clock-out failed', description: err?.response?.data?.detail || '' }); }
    finally { setClockBusy(false); }
  };

  const saveProfile = async () => {
    try {
      const emp = prof?.employee;
      await hrmsUpdateEmployee(emp.id, { mobile: edit.mobile, location: edit.location });
      toast({ title: 'Profile updated' });
      setEdit(null); load();
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail || '' }); }
  };

  const submitLeave = async () => {
    if (!leave.from_date || !leave.to_date) { toast({ title: 'Please pick both dates' }); return; }
    try {
      await hrmsRequestLeave(leave);
      toast({ title: 'Leave request submitted' });
      setLeaveOpen(false); setLeave({ leave_type: 'Casual', from_date: '', to_date: '', reason: '' });
      load();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || '' }); }
  };

  if (!prof) return <div className="text-slate-500 text-sm">Loading…</div>;
  const emp = prof.employee;

  return (
    <div data-testid="me-page">
      <h1 className="text-2xl font-extrabold flex items-center gap-2"><User className="h-6 w-6 text-orange-500" /> My workspace</h1>
      <p className="text-sm text-slate-500 mt-1">Your profile, leaves and workspace details.</p>

      {!emp && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900 text-sm">
          You are signed in as <b>{prof.user.email}</b> but no employee profile exists yet. Ask HR to onboard you to access self-service.
        </div>
      )}

      {emp && (
        <>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 md:col-span-1" data-testid="me-clock-card">
              <div className="text-xs uppercase tracking-widest text-slate-300">Today · {todayStr()}</div>
              <div className="mt-2 text-3xl font-extrabold">
                {todayRec?.clock_out ? `${todayRec.hours || 0}h` : todayRec?.clock_in ? 'Working…' : '—'}
              </div>
              <div className="text-xs text-slate-300 mt-1">
                {todayRec?.clock_in ? `In: ${new Date(todayRec.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Not clocked in yet'}
                {todayRec?.clock_out ? ` · Out: ${new Date(todayRec.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
              </div>
              <div className="mt-4 flex gap-2">
                {!todayRec?.clock_in && <Button onClick={clockIn} disabled={clockBusy} className="bg-emerald-500 hover:bg-emerald-600 flex-1" data-testid="me-clock-in"><Play className="h-4 w-4 mr-1" /> Clock in</Button>}
                {todayRec?.clock_in && !todayRec?.clock_out && <Button onClick={clockOut} disabled={clockBusy} className="bg-orange-500 hover:bg-orange-600 flex-1" data-testid="me-clock-out"><Square className="h-4 w-4 mr-1" /> Clock out</Button>}
                {todayRec?.clock_out && <div className="text-emerald-300 text-sm flex items-center gap-1"><Clock className="h-4 w-4" /> All done for today</div>}
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-6 md:col-span-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{emp.employee_id}</div>
                  <h2 className="text-xl font-bold text-slate-900">{emp.full_name}</h2>
                  <div className="text-sm text-slate-600">{emp.designation}{emp.department ? ` · ${emp.department}` : ''}</div>
                </div>
                <Button variant="outline" onClick={() => setEdit({ mobile: emp.mobile || '', location: emp.location || '' })} data-testid="me-edit-profile">Edit contact</Button>
              </div>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                {[
                  ['Email', emp.email], ['Mobile', emp.mobile || '—'], ['Location', emp.location || '—'],
                  ['Manager', emp.manager_email || '—'], ['Joining date', emp.joining_date || '—'], ['Status', emp.status || 'active'],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">{k}</div>
                    <div className="text-slate-900 font-semibold break-all">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <h2 className="text-lg font-bold">My leaves</h2>
            <Button onClick={() => setLeaveOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="me-apply-leave"><CalendarPlus className="h-4 w-4 mr-1" /> Apply for leave</Button>
          </div>
          <div className="mt-3 rounded-2xl border bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700"><tr>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">From</th>
                <th className="text-left px-4 py-3">To</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr></thead>
              <tbody>
                {leaves.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No leaves yet.</td></tr>}
                {leaves.map((l) => (
                  <tr key={l.id} className="border-t">
                    <td className="px-4 py-3">{l.leave_type}</td>
                    <td className="px-4 py-3">{l.from_date}</td>
                    <td className="px-4 py-3">{l.to_date}</td>
                    <td className="px-4 py-3 max-w-xs whitespace-normal">{l.reason || '—'}</td>
                    <td className="px-4 py-3"><Badge className={l.status === 'approved' ? 'bg-emerald-500' : l.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'}>{l.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white p-5 flex items-center justify-between" data-testid="me-onboarding-cta">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-orange-100">New joiner</div>
              <div className="font-bold text-lg">Your onboarding kit is waiting</div>
              <div className="text-xs text-orange-100">Complete your checklist, upload documents and watch the welcome video.</div>
            </div>
            <a href="/hrms/me/onboarding" className="rounded-lg bg-white text-orange-600 font-semibold px-4 py-2 text-sm hover:bg-orange-50" data-testid="me-onboarding-cta-link">Open kit →</a>
          </div>

          <h2 className="mt-8 text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 text-orange-500" /> My referrals</h2>
          <p className="text-sm text-slate-500 mt-1">Share your personal careers link — earn rewards as your referrals progress.</p>
          {referralLink && (
            <div className="mt-3 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 p-5" data-testid="me-referral-card">
              <div className="text-[11px] uppercase tracking-widest text-orange-700 font-semibold">Your referral link</div>
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 min-w-0 truncate font-mono text-sm text-slate-800 bg-white/70 border border-orange-200 rounded-lg px-3 py-2" data-testid="me-referral-link">{referralLink.careers_link}</div>
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(referralLink.careers_link); toast({ title: 'Link copied' }); }} data-testid="me-referral-copy"><Copy className="h-4 w-4 mr-1" /> Copy</Button>
                {navigator.share && <Button variant="outline" onClick={() => navigator.share({ title: 'Hipro Technologies · Careers', url: referralLink.careers_link })}><Share2 className="h-4 w-4 mr-1" /> Share</Button>}
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg bg-white/60 border border-orange-200 p-3"><div className="text-[10px] uppercase text-slate-500">Applied</div><div className="text-xl font-extrabold text-slate-900">{referrals.stats?.total || 0}</div></div>
                <div className="rounded-lg bg-white/60 border border-orange-200 p-3"><div className="text-[10px] uppercase text-slate-500">Interviewed</div><div className="text-xl font-extrabold text-slate-900">{referrals.stats?.interviewed || 0}</div></div>
                <div className="rounded-lg bg-white/60 border border-orange-200 p-3"><div className="text-[10px] uppercase text-slate-500">Joined</div><div className="text-xl font-extrabold text-emerald-700">{referrals.stats?.joined || 0}</div></div>
                <div className="rounded-lg bg-white/60 border border-orange-200 p-3"><div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Trophy className="h-3 w-3 text-amber-500" /> Rewards</div><div className="text-xl font-extrabold text-orange-600">₹{((referrals.stats?.rewards_paid || 0) + (referrals.stats?.rewards_pending || 0)).toLocaleString('en-IN')}</div><div className="text-[10px] text-slate-500">₹{(referrals.stats?.rewards_paid || 0).toLocaleString('en-IN')} paid</div></div>
              </div>
              {referrals.referrals.length > 0 && (
                <div className="mt-4 rounded-xl bg-white border border-orange-100">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 uppercase border-b">
                      <tr><th className="text-left py-2 px-3">Candidate</th><th className="text-left py-2 px-3">Role</th><th className="text-left py-2 px-3">Stage</th><th className="text-right py-2 px-3">Reward</th></tr>
                    </thead>
                    <tbody>
                      {referrals.referrals.slice(0, 10).map((r) => (
                        <tr key={r.id} className="border-t">
                          <td className="py-2 px-3 font-medium">{r.full_name}</td>
                          <td className="py-2 px-3">{r.job_title}</td>
                          <td className="py-2 px-3 text-xs capitalize">{(r.stage || '').replace(/_/g, ' ')}</td>
                          <td className="py-2 px-3 text-right font-semibold text-orange-600">{r.reward_amount ? `₹${r.reward_amount.toLocaleString('en-IN')}` : '—'}<div className="text-[10px] text-slate-500 capitalize">{r.reward_status}</div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <h2 className="mt-8 text-lg font-bold">My payslips</h2>
          <div className="mt-3 rounded-2xl border bg-white overflow-hidden" data-testid="me-payslips">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700"><tr>
                <th className="text-left px-4 py-3">Month</th>
                <th className="text-left px-4 py-3">Payslip ID</th>
                <th className="text-left px-4 py-3">Paid days</th>
                <th className="text-right px-4 py-3">Gross</th>
                <th className="text-right px-4 py-3">Net</th>
                <th className="text-left px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {payslips.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-10">No payslips yet. HR will process this month's payroll soon.</td></tr>}
                {payslips.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="px-4 py-3 font-semibold">{p.month}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.payslip_id}</td>
                    <td className="px-4 py-3">{p.paid_days}/{p.total_days}</td>
                    <td className="px-4 py-3 text-right">{(p.gross || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-600">{(p.net || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3"><Button variant="outline" onClick={() => generatePayslipPdf(p)} className="h-8 px-2" data-testid={`me-payslip-pdf-${p.month}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={!!edit} onOpenChange={(v) => { if (!v) setEdit(null); }}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Edit contact details</DialogTitle>
          {edit && (
            <div className="mt-2 space-y-3">
              <div><Label>Mobile</Label><Input value={edit.mobile} onChange={(e) => setEdit({ ...edit, mobile: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
                <Button onClick={saveProfile} className="bg-orange-500 hover:bg-orange-600">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-md">
          <DialogTitle>Apply for leave</DialogTitle>
          <div className="mt-2 space-y-3">
            <div>
              <Label>Type</Label>
              <select value={leave.leave_type} onChange={(e) => setLeave({ ...leave, leave_type: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="me-leave-type">
                <option>Casual</option><option>Sick</option><option>Earned</option><option>Unpaid</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From</Label><Input type="date" value={leave.from_date} onChange={(e) => setLeave({ ...leave, from_date: e.target.value })} data-testid="me-leave-from" /></div>
              <div><Label>To</Label><Input type="date" value={leave.to_date} onChange={(e) => setLeave({ ...leave, to_date: e.target.value })} data-testid="me-leave-to" /></div>
            </div>
            <div><Label>Reason</Label><Textarea rows={3} value={leave.reason} onChange={(e) => setLeave({ ...leave, reason: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setLeaveOpen(false)}>Cancel</Button>
              <Button onClick={submitLeave} className="bg-orange-500 hover:bg-orange-600" data-testid="me-leave-submit">Submit request</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MePage;
